import React, {useState, useEffect} from "react";
import {Row, Col, Container, Button} from "react-bootstrap";
import {FaEnvelope, FaPhoneAlt} from "react-icons/fa";
import {useDispatch, useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import {useQuery} from "react-apollo";
import {hotjar} from "react-hotjar";

import {RootState} from "../state/rootReducer";
import {PRODUCT, Product} from "../graphql/Products";
import {increaseCheckoutQuantity} from "../state/slices/checkoutSlice";

import TagManager from "react-gtm-module";
import LoadingLogo from "../components/app/LoadingLogo";
import {getMetaText} from "../AppText";
import {Helmet} from "react-helmet";
import {howItWorks} from "./whiskyPromoData";
import styles from "./WhiskyPromo.module.scss";

const WhiskyPromo = () => {
  hotjar.initialize(2882523, 6);

  const dispatch = useDispatch();
  const history = useHistory();
  const shop = useSelector((state: RootState) => state.shop);
  const currency = useSelector((state: RootState) => state.shop.currency);

  // const productId = 'collective-tasting-pack-feb-2024-1';
  // const productId = 'caskshare-tasting-pack-best-of-2024';
  const productId = 'caskshare-sample-series-spring-2025';
  const {data, loading} = useQuery(PRODUCT, {
    variables: {
      shop: shop.id,
      id: productId,
    },
  });

  const [product, setProduct] = useState<Product | undefined>(undefined);
  const [price, setPrice] = useState(0);

  // get url path and store in a session cookie for attribution
  const path = window.location.pathname;
  const pathArray = path.split("/");
  const campaignCode = pathArray[pathArray.length - 1];

  // set cookie for 30 days
  const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "expires=" + date.toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/";
  };
  // set cookie for campaign code
  setCookie("campaign_code", campaignCode, 30);

  useEffect(() => {
    if (data?.product != null) {
      setProduct(data?.product);
    }
  }, [data?.product]);

  useEffect(() => {
    if (product?.defaultVariant) {
      setPrice(product.defaultVariant.price.amount);
    }
  }, [product?.defaultVariant]);

  if (loading || product == null) {
    return <LoadingLogo />;
  }

  const addProductToBasket = () => {
    dispatch(increaseCheckoutQuantity({product: product}));
    history.push("/basket");
  };


  // Google Analytics 4
  TagManager.dataLayer({dataLayer: {ecommerce: null}});
  var tagManagerArgs = {
    dataLayer: {
      event: "view_item",
      ecommerce: {
        currency: currency,
        items: [
          {
            item_id: `${ product.seller.title }-${ product.sku }`,
            item_brand: product.vendor,
            item_name: product.title,
            price: price,
            item_category: "product",
          },
        ],
      },
    },
  };
  TagManager.dataLayer(tagManagerArgs);

  return (
    <>
      <Helmet>
        <title>{getMetaText("WELCOME_PAGE", "title")}</title>
        <link rel="canonical" href={window.location.href} />
        <meta
          name="keywords"
          content={getMetaText("WELCOME_PAGE", "keywords")}
        />
        <meta
          name="description"
          content={getMetaText("WELCOME_PAGE", "description")}
        />
        <meta name="author" content={getMetaText("WELCOME_PAGE", "author")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:title"
          content={getMetaText("WELCOME_PAGE", "title")}
        />
        <meta
          property="og:description"
          content={getMetaText("WELCOME_PAGE", "description")}
        />
        <meta
          property="og:image"
          content={
            window.location.origin + require("../assets/home-page/splash.jpg")
          }
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@thecaskshare" />
        <meta
          name="twitter:title"
          content={getMetaText("WELCOME_PAGE", "title")}
        />
        <meta
          name="twitter:description"
          content={getMetaText("WELCOME_PAGE", "description")}
        />
        <meta
          name="twitter:image"
          content={
            window.location.origin + require("../assets/home-page/splash.jpg")
          }
        />
      </Helmet>
      <Container className={styles.pageWrapper} fluid>
        <div className={styles.BGWrap}>
          <Container
            className={`${ styles.sectionWrap } ${ styles.caskDetailSection }`}
          >
            <Row className={`${ styles.caskDetailRow } ${ styles.whiteBG }`}>
              <div className={styles.top}>
                <div className={styles.headLine}>
                  <h3>
                    your invitation to a tasting of <br /> <span>The finest single cask whiskies</span>
                  </h3>
                  <img
                    className={styles.iconsOfWhisky}
                    src={require("../assets/images/IoW25-Scotland-IndependentBottler.svg")}
                  />
                </div>
              </div>
              <div className={styles.left}>
                <div className={styles.inner}>
                  <div className={styles.bulletPoints}>
                    <div className={styles.item}>
                      <p className={styles.bulletPoint} /> <p>5 x 30ml bottles of the finest single cask whisky</p>
                    </div>
                    <div className={styles.item}>
                      <p className={styles.bulletPoint} /> <p>PLUS an online Whisky Tasting Session with our resident whisky experts</p>
                    </div>
                    <div className={styles.item}>
                      <p className={styles.bulletPoint} /> <p>We'll show you why we think single cask whisky is the ultimate whisky</p>
                    </div>
                    <div className={styles.item}>
                      <p className={styles.bulletPoint} /> <p>All for ONLY £20</p>
                    </div>
                  </div>
                  <div className={styles.priceWrap}>
                    <p><span> 5 unique whiskies plus</span> <br /> online tasting session <br /> for only</p>
                    <p className={styles.price}>£20</p>
                    <p className={styles.delivery}>including free delivery</p>
                  </div>
                </div>
              </div>
              <div className={styles.btnMobile}>
                <Button
                  className={styles.btnPrimary}
                  onClick={addProductToBasket}
                  disabled={product.quantity <= 0}
                >
                  {product.quantity >= 1 ? "Add to basket" : "Sold Out"}
                </Button>
              </div>
              <div className={styles.right}>
                <div className={styles.inner}>
                  <div className={styles.statement}>
                    <p className={styles.title}>HONESTY STATEMENT - So what's the catch?</p>
                    <p>So we're sending you 5 drams of the finest single cask whisky AND inviting you to our online whisky tasting session all for only £20. There's no subscription sign up or any other obligation to buy so you're probably thinking "what's the catch?". It's simple, we think once you've tasted one-of-a kind single cask whisky from Caskshare you won't want to buy your whisky from anywhere else.</p>
                  </div>
                  <Button
                    className={styles.btnPrimary}
                    onClick={addProductToBasket}
                    disabled={product.quantity <= 0}
                  >
                    {product.quantity >= 1 ? "Add to basket" : "Sold Out"}
                  </Button>
                  <div className={styles.review}>
                    <p className={`${ styles.description }`}>
                      "I first discovered the wonderful world of Single <br /> Cask whisky at a Caskshare tasting and <br /> for that I shall be forever grateful!"
                    </p>
                    <p className={styles.sign}>JAck Cartwright, huddersfield</p>
                  </div>
                </div>
              </div>
            </Row>
            <div className={styles.spacer} />
          </Container>
        </div>
        <div className={styles.beigeBG}>
          <Container className={styles.sectionWrap}>
            <div className={styles.spacer} />
            <Row className={styles.howItWorksSection}>
              <div className={styles.spacer} />
              <Col xs={12} className={styles.wrap}>
                <div className={styles.left}>
                  <div className={styles.title}>
                    <h3>
                      how it works...
                    </h3>
                  </div>
                </div>
                <div className={styles.right}>
                  <div className={styles.imgTop}>
                    <img
                      src={require("../assets/campaign-pages/CS_JAN24_-107.jpg")}
                      alt="Caskshare Collective Members Enjoying Single Cask Whisky"
                    />
                  </div>
                </div>
                <div className={styles.left}>
                  <div className={styles.benefits}>
                    {howItWorks()
                      .map((item: any) => (
                        <div key={item.id} className={styles.inner}>
                          <h5>
                            {item.title} <br />
                            <span>{item.title2}</span>
                          </h5>
                          <p>{item.text}</p>
                        </div>
                      ))}
                    <Button
                      className={styles.btnPrimary}
                      onClick={addProductToBasket}
                      disabled={product.quantity <= 0}
                    >
                      {product.quantity >= 1 ? "Add to basket" : "Sold Out"}
                    </Button>
                  </div>
                </div>
                <div className={styles.right}>
                  <div className={styles.imgBottom}>
                    <img
                      src={require("../assets/campaign-pages/CaskShare_August 2023)-21.jpg")}
                      alt="Caskshare Collective Member Enjoying Single Cask Whisky"
                    />
                  </div>
                </div>
                <div className={styles.spacer} />
              </Col>
            </Row>
          </Container>
        </div>
        <div className={''}>
          <Container className={styles.sectionWrap}>
            <div className={styles.spacer} />
            <Row className={styles.whiskyEnthusiastsSection}>
              <div className={styles.inner}>
                <div className={styles.headLine}>
                  <h3>
                    WHY DO WHISKY ENTHUSIASTS <br /> <span>LOVE SINGLE CASK WHISKY?</span>
                  </h3>
                </div>
                <Col className={`${ styles.wrap } ${ styles.rare }`}>
                  <div className={styles.imgWrap}>
                    <img
                      src={require("../assets/campaign-pages/CS_JAN24_-105.jpg")}
                      alt="Caskshare Collective Member Enjoying Single Cask Whisky"
                    />
                  </div>
                  <div className={styles.contentWrap}>
                    <h5>RARE & LIMITED <br /> <span>EDITIONS</span></h5>
                    <p>
                      Single cask whiskies are sought after because of their rarity, as each cask
                      produces a limited number of bottles, making each cask a limited edition.
                    </p>
                  </div>
                </Col>
                <Col className={`${ styles.wrap } ${ styles.reverse }`}>
                  <div className={styles.imgWrap}>
                    <img
                      src={require("../assets/campaign-pages/DSC01228.jpg")}
                      alt="Caskshare Collective Member Enjoying Single Cask Whisky"
                    />
                  </div>
                  <div className={styles.contentWrap}>
                    <h5>UNIQUE TASTING <br /> <span>PROFILE</span></h5>
                    <p>
                      Single Cask is often the “go to” for whisky enthusiasts because each Single
                      Cask has it’s own variations in spirit, wood (the chosen cask) and ageing.
                      <br /> This gives each cask a one-of-a-kind tasting profile that’s coveted by whisky
                      fans who appreciate the unique flavour complexities of every single cask.</p>
                  </div>
                </Col>
                <Col className={`${ styles.wrap } ${ styles.tasting }`}>
                  <div className={styles.imgWrap}>
                    <img
                      src={require("../caskshare-v3/assets/collective-page/collective_events.jpg")}
                      alt="Caskshare Collective Member Enjoying Single Cask Whisky"
                    />
                  </div>
                  <div className={styles.contentWrap}>
                    <h5>WE LOVE TASTINGS & WE LOVE TO <br /> <span>SHARE OUR AMAZING WHISKY</span></h5>
                    <p>
                      Historically, the chance to experience Single Cask whisky has been reserved
                      for a select group of whisky drinkers but at Caskshare our mission is to bring
                      the wonder of the single cask experience to all, because we believe whisky
                      that tastes this good should always be shared.
                    </p>
                  </div>
                </Col>
              </div>
            </Row>
          </Container>
        </div>
        <div className={''}>
          <Container className={styles.sectionWrap}>
            <div className={styles.spacer} />
            <Row className={styles.bookTastingSection}>
              <div className={styles.inner}>
                <div className={styles.headLine}>
                  <h3>
                    BOOK YOUR WHISKY TASTING <br /> <span>& YOU’LL RECEIVE...</span>
                  </h3>
                </div>
                <div className={styles.pressSection}>
                  <div className={styles.imgWrap}>
                    <img
                      src={require("../assets/campaign-pages/CS_JAN24_-100.jpg")}
                      alt="man enjoying single cask whisky"
                    />
                  </div>
                  <div className={styles.head}>
                    <h5>
                      5 UNIQUE WHISKIES PLUS <br /> <span>ONLINE TASTING SESSION
                        NOW ONLY <span>£20</span></span>
                    </h5>
                    <Button
                      className={styles.btnBlackOutline}
                      onClick={addProductToBasket}
                      disabled={product.quantity <= 0}
                    >
                      {product.quantity >= 1 ? "Add to basket" : "Sold Out"}
                    </Button>
                    <p>INCLUDING FREE DELIVERY</p>
                  </div>
                  <div className={styles.content}>
                    <p>
                      Once you’ve received your whisky
                      tasting pack, we’ll send you an
                      email to arrange a whisky tasting
                      date and time to suit your diary.
                    </p>
                    <p>Then join us online for the ultimate whisky tasting experience.</p>
                    <p><strong>This is not a subscription service </strong>
                      and attending the Caskshare Whisky
                      Tasting does not obligate you to
                      further purchases... just to enjoy great
                      company and wonderful whisky!</p>
                  </div>
                </div>
              </div>
            </Row>
          </Container>
        </div>
        <Row className={styles.startCollection}>
          <Col xs={12} className={styles.inner}>
            <span />
            <h2>Need help?</h2>
            <h5>get in touch...</h5>
            <div className={styles.contactDetails}>
              <FaEnvelope />{" "}
              <a href="mailto:contact@caskshare.com">contact@caskshare.com</a>
            </div>
            <div className={styles.contactDetails}>
              <FaPhoneAlt /> <a href="tel:00441313812275">+44-131-381-2275</a>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default WhiskyPromo;
